import m from "mithril";
import { getUserData, getSyncUserData } from "../utils.js";
import toast from "../js/toaster.js";

const BankDepositHistory = {
    bankingHistory: [],
    loading: false,
    error: null,
    subunitId: null,
    subunitName: '',

    oninit: function () {
        const userData = getSyncUserData();
        this.subunitId = userData.subunit_id;
        this.subunitName = userData.subunit_name
        this.loadBankingHistory();
    },

    loadBankingHistory: async function () {
        this.loading = true;
        this.error = null;
        m.redraw();

        try {
            const userData = getSyncUserData();
            const token = userData.jwt;

            const response = await fetch('/api/coordinator-management/banking-history', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (result.success) {
                this.bankingHistory = result.data;
            } else {
                this.error = result.error || 'Failed to load banking history';
                toast.error(this.error);
            }
        } catch (error) {
            console.error('Error loading banking history:', error);
            this.error = 'Failed to load banking history: ' + error.message;
            toast.error(this.error);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    },

    formatDate: function (dateString) {
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    printReceipt: function (transaction) {
        const receiptWindow = window.open('', '_blank', 'width=600,height=800');
        const receiptHTML = this.generateReceiptHTML(transaction);

        receiptWindow.document.write(receiptHTML);
        receiptWindow.document.close();

        // Wait for content to load then print
        receiptWindow.onload = function () {
            receiptWindow.print();
            // Close after printing (optional)
            // receiptWindow.close();
        };
    },

    generateReceiptHTML: function (transaction) {
        const depositedAmount = Math.abs(transaction.collection_amount);
        const receiptNumber = `SETOR-${transaction.id.toString().padStart(6, '0')}`;

        return `
        <!DOCTYPE html>
        <html lang="id">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Bukti Setoran - ${receiptNumber}</title>
            <style>
                body {
                    font-family: 'Arial', sans-serif;
                    margin: 0;
                    padding: 20px;
                    background-color: #f5f5f5;
                }
                .receipt {
                    max-width: 400px;
                    margin: 0 auto;
                    background: white;
                    border: 1px solid #ddd;
                    padding: 30px;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                .header {
                    text-align: center;
                    border-bottom: 2px solid #333;
                    padding-bottom: 20px;
                    margin-bottom: 20px;
                }
                .header h1 {
                    margin: 0;
                    color: #333;
                    font-size: 28px;
                    font-weight: bold;
                }
                .header p {
                    margin: 5px 0;
                    color: #666;
                    font-size: 16px;
                    font-weight: 500;
                }
                .receipt-number {
                    font-weight: bold;
                    color: #d32f2f;
                    font-size: 16px;
                }
                .details {
                    margin: 20px 0;
                }
                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    margin: 8px 0;
                    padding: 5px 0;
                    border-bottom: 1px solid #eee;
                }
                .detail-label {
                    font-weight: bold;
                    color: #555;
                }
                .detail-value {
                    text-align: right;
                    color: #333;
                }
                .amount {
                    font-size: 18px;
                    font-weight: bold;
                    color: #2e7d32;
                    text-align: center;
                    margin: 20px 0;
                    padding: 15px;
                    background: #e8f5e8;
                    border-radius: 5px;
                    border: 2px solid #2e7d32;
                }
                .footer {
                    margin-top: 30px;
                    text-align: center;
                    border-top: 1px solid #ddd;
                    padding-top: 20px;
                }
                .signature {
                    margin-top: 40px;
                    display: flex;
                    justify-content: space-between;
                }
                .signature-box {
                    width: 45%;
                    text-align: center;
                    border-top: 1px solid #333;
                    padding-top: 10px;
                    font-size: 12px;
                }
                @media print {
                    body { background: white; }
                    .receipt { box-shadow: none; border: none; }
                }
            </style>
        </head>
        <body>
            <div class="receipt">
                <div class="header">
                    <h3>Bukti Setoran Dana</h3>
                    <p>${this.subunitName}</p>
                    <p class="receipt-number">${receiptNumber}</p>
                </div>

                <div class="details">
                    <div class="detail-row">
                        <span class="detail-label">Tanggal:</span>
                        <span class="detail-value">${this.formatDate(transaction.created_at)}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Koordinator:</span>
                        <span class="detail-value">${transaction.officer_name}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Kode:</span>
                        <span class="detail-value">${transaction.officer_code}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Jenis Transaksi:</span>
                        <span class="detail-value">Setoran ke Bank</span>
                    </div>
                </div>

                <div class="amount">
                    JUMLAH SETORAN<br>
                    <strong>${this.formatCurrency(depositedAmount)}</strong>
                </div>

                <div class="footer">
                    <p><em>Terima kasih atas kerja sama Anda</em></p>
                    <p>Dokumen ini adalah bukti sah setoran dana</p>
                </div>

                <div class="signature">
                    <div class="signature-box">
                        <strong>Koordinator</strong><br>
                        ${transaction.officer_name}
                    </div>
                    <div class="signature-box">
                        <strong>Bendahara</strong><br>
                        ${this.subunitName}
                    </div>
                </div>
            </div>
        </body>
        </html>
        `;
    },

    view: function () {
        const totalDeposited = this.bankingHistory.reduce((sum, transaction) =>
            sum + Math.abs(transaction.collection_amount), 0);

        return m("div", { class: "max-w-6xl mx-auto p-6" }, [
            m("div", { class: "mb-8" }, [
                m("div", { class: "flex items-center space-x-4 mb-2" }, [
                    m("div", { class: "w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg" }, [
                        m("i", { class: "ri-bank-line text-white text-xl" })
                    ]),
                    m("div", [
                        m("h1", { class: "text-3xl font-bold text-gray-900 dark:text-white" }, "Riwayat Setoran Ke Kantor"),
                        m("p", { class: "text-lg text-gray-600 dark:text-gray-400" }, "Histori semua transaksi setoran dana koordinator ke bank")
                    ])
                ]),
                m("div", { class: "h-1 w-20 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full" })
            ]),

            // Summary Cards
            m("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" }, [
                m("div", { class: "bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-6 border border-blue-200 dark:border-blue-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-blue-600 dark:text-blue-400" }, "Total Setoran"),
                            m("p", { class: "text-2xl font-bold text-blue-900 dark:text-blue-100" }, this.formatCurrency(totalDeposited))
                        ]),
                        m("div", { class: "w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-money-dollar-circle-line text-white text-xl" })
                        ])
                    ])
                ]),
                m("div", { class: "bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-6 border border-green-200 dark:border-green-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-green-600 dark:text-green-400" }, "Jumlah Transaksi"),
                            m("p", { class: "text-2xl font-bold text-green-900 dark:text-green-100" }, this.bankingHistory.length)
                        ]),
                        m("div", { class: "w-12 h-12 bg-green-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-file-list-line text-white text-xl" })
                        ])
                    ])
                ]),
                m("div", { class: "bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-6 border border-purple-200 dark:border-purple-700" }, [
                    m("div", { class: "flex items-center justify-between" }, [
                        m("div", [
                            m("p", { class: "text-sm font-medium text-purple-600 dark:text-purple-400" }, "Koordinator Terlibat"),
                            m("p", { class: "text-2xl font-bold text-purple-900 dark:text-purple-100" },
                                new Set(this.bankingHistory.map(t => t.officer_id)).size
                            )
                        ]),
                        m("div", { class: "w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center" }, [
                            m("i", { class: "ri-user-star-line text-white text-xl" })
                        ])
                    ])
                ])
            ]),

            // Error message
            this.error && m("div", { class: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" }, this.error),

            // History table
            m("div", { class: "bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider" }, "Tanggal"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider" }, "Koordinator"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider" }, "Kode"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider" }, "Jumlah Setoran"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider" }, "Aksi")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.loading && !this.bankingHistory.length && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "5" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-3" }, [
                                        m("i", { class: "ri-loader-4-line animate-spin text-3xl text-blue-600" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Memuat data riwayat setoran...")
                                    ])
                                ])
                            ]),
                            !this.loading && this.bankingHistory.length === 0 && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "5" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-3" }, [
                                        m("i", { class: "ri-bank-line text-4xl text-gray-400" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Belum ada riwayat setoran")
                                    ])
                                ])
                            ]),
                            this.bankingHistory.map((transaction, index) => {
                                const depositedAmount = Math.abs(transaction.collection_amount);
                                return m("tr", {
                                    key: transaction.id,
                                    class: index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-750"
                                }, [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" }, [
                                        m("div", { class: "flex flex-col" }, [
                                            m("span", this.formatDate(transaction.created_at).split(',')[0]),
                                            m("span", { class: "text-xs text-gray-500 dark:text-gray-400" }, this.formatDate(transaction.created_at).split(',')[1]?.trim())
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white" }, [
                                        m("div", { class: "flex items-center space-x-3" }, [
                                            m("div", { class: "w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center" }, [
                                                m("i", { class: "ri-user-star-line text-blue-600 dark:text-blue-400 text-sm" })
                                            ]),
                                            m("span", transaction.officer_name)
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                        m("span", { class: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300" },
                                            transaction.officer_code
                                        )
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-bold" }, [
                                        m("span", { class: "text-green-600 dark:text-green-400" }, this.formatCurrency(depositedAmount))
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("button", {
                                            onclick: () => this.printReceipt(transaction),
                                            class: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                        }, [
                                            m("i", { class: "ri-printer-line mr-1.5" }),
                                            "Cetak Bukti"
                                        ])
                                    ])
                                ]);
                            })
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default BankDepositHistory;